import { AnyAction } from 'redux';
import * as actionTypes from '../../../../models/actionTypes';
import { ISharedState, initialSharedState } from '../../../../models/reduxState';
import { getValidDeepLink } from '../../../../utilities';

const handleContactQueryUpdate = (state: ISharedState = initialSharedState, action: AnyAction): ISharedState => {
  return {
    ...state,
    contactSearchedQuery: action.query,
  };
};

const handleUpdateRequestVerificationToken = (state: ISharedState = initialSharedState, action: AnyAction): ISharedState => {
  return {
    ...state,
    requestVerificationToken: action.formToken,
  };
};

const handleInitDataGrabSuccess = (state: ISharedState = initialSharedState, action: AnyAction): ISharedState => {
  let isUnAuthSupportEnabled = false;
  if (action.supportSettingsConfig && action.supportSettingsConfig.UnAuthSupportAllowedAppVers) {
    isUnAuthSupportEnabled = action.supportSettingsConfig.UnAuthSupportAllowedAppVers
      .map((v: string) => v ? v.toLowerCase() : '')
      .indexOf((action.sourceApp || action.appVer || '').toLowerCase()) > -1 &&
      action.supportSettingsConfig.IsUnAuthSupportEnabled;
  }
  const isDeepLinkEnabled = !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsDeepLinkEnabled;
  const contactSearchedQuery = getValidDeepLink(isDeepLinkEnabled, state.contactSearchedQuery);
  return {
    ...state,
    isUserNoticeEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsUserNoticeEnabled,
    userNoticeAlchemyQuery:
      !!action.supportSettingsConfig && !!action.supportSettingsConfig.UserNoticeAlchemyQuery
        ? action.supportSettingsConfig.UserNoticeAlchemyQuery
        : '',
    isUnAuthSupportEnabled: isUnAuthSupportEnabled,
    isDeepLinkEnabled: isDeepLinkEnabled,
    isDeepLinkQuery: !!contactSearchedQuery && isDeepLinkEnabled,
    contactSearchedQuery: contactSearchedQuery,
    safActionMaxRetries: !!action.supportSettingsConfig ? action.supportSettingsConfig.SAFActionMaxRetries : 0,
    hideSelfHelpSmcIvr: !!action.supportSettingsConfig && !!action.supportSettingsConfig.HideSelfHelpSmcIvr,
    showSupportNav: !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShowSupportNav,
    useIconsForSupportNav: !!action.supportSettingsConfig && !!action.supportSettingsConfig.UseIconsForSupportNav,
    isSearchSessionResetEnabled:
      !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsSearchSessionResetEnabled,
    shouldShowOutdatedDevicesAlert:
      !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShouldShowOutdatedDevicesAlert,
    shouldShowVsbUpsellAlert: !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShouldShowVSBUpsellAlert,
    deviceMinCountToShowExpandIcon: !!action.supportSettingsConfig
      ? action.supportSettingsConfig.DeviceMinCountToShowExpandIcon
      : 3,
    deviceMaxCount: !!action.supportSettingsConfig ? action.supportSettingsConfig.DeviceMaxCount : 10,
    howToUpdateWindowsUrl: !!action.supportSettingsConfig ? action.supportSettingsConfig.HowToUpdateWindowsUrl : '',
    windowsUpdateInvokingUrl: !!action.supportSettingsConfig
      ? action.supportSettingsConfig.WindowsUpdateInvokingUrl
      : '',
    compareM365PlansUrl: !!action.supportSettingsConfig ? action.supportSettingsConfig.CompareM365PlansUrl : '',
    openLinksInNewTab: !!action.supportSettingsConfig && !!action.supportSettingsConfig.OpenLinksInNewTab,
    isLogBundleLoadSuccessEnabled:
      !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsLogBundleLoadSuccessEnabled,
    useAnnouncementFloat: !!action.supportSettingsConfig && !!action.supportSettingsConfig.UseAnnouncementFloat,
    isBusinessAssistUXEnabled:
      !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsBusinessAssistUXEnabled,
    isFeedbackCoachmarkEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShouldShowFeedbackCoachMark,
    isFeedbackTicketCreation: !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShouldShowFeedbackTicketCreation,
    isPostAlchemyFeedbackEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsPostAlchemyFeedbackEnabled,
    showWin10EndOfSupportMessage: !!action.supportSettingsConfig && !!action.supportSettingsConfig.ShowWin10EndOfSupportMessage,
  };
};

const handleSetContactSearchedQueryText = (state: ISharedState, action: AnyAction) => {
  return {
    ...state,
    contactSearchedQuery: action.query,
  };
};

const handleDeepLinkUpdate = (state: ISharedState, action: AnyAction) => {
  return {
    ...state,
    isDeepLinkQuery: false,
  };
};

const handleIsAutoSuggestCardSearch = (state: ISharedState, action: AnyAction) => {
  return {
    ...state,
    isAutoSuggestCardSearch: !!action.isAutoSuggestCardSearch,
  };
};

/**
 * Whenever you define a new property to ISharedState, consider the need to reset it if it's impacted by search or ticket creation
 */
const handleSearchSessionReset = (state: ISharedState = initialSharedState) => {
  return {
    ...state,
    contactSearchedQuery: initialSharedState.contactSearchedQuery,
    isAutoSuggestCardSearch: initialSharedState.isAutoSuggestCardSearch,
    isDeepLinkQuery: initialSharedState.isDeepLinkQuery,
    deeplinkPath: '',
  };
};

export const handleFetchRegionInfoSuccess = (
  state: ISharedState = initialSharedState,
  action: AnyAction
): ISharedState => {
  if (action.response) {
    return {
      ...state,
      regionInfo: action.response,
    };
  }
  return state;
};

export const handleRegionInfoError = (
  state: ISharedState = initialSharedState,
): ISharedState => {
  return {
    ...state,
    regionInfo: null,
  };
};

const handleDeepLinkPathUpdate = (state: ISharedState, action: AnyAction) => {
  const deeplinkPath = action.payload as string;
  if (state.deeplinkPath !== deeplinkPath) {
    return { ...state, deeplinkPath };
  }
  return state;
};

const resetContactUsFocus = (state: ISharedState, action: AnyAction) => {
  return {
    ...state,
    shouldResetFocus: action.payload,
  };
};

const handleToggleCoachmark = (state: ISharedState, action: AnyAction) => {
  return {
    ...state,
    isFeedbackCoachmarkShowing: action.isFeedbackCoachmarkShowing
  }
}

export const SharedStateReducer = (state: ISharedState = initialSharedState, action: AnyAction): ISharedState => {
  switch (action.type) {
    case actionTypes.TOGGLE_COACHMARK:
      return handleToggleCoachmark(state, action);
    case actionTypes.FETCH_ON_SESSION_START:
      return handleContactQueryUpdate(state, action);
    case actionTypes.FETCH_CSRF_FORM_TOKEN_SUCCESS:
      return handleUpdateRequestVerificationToken(state, action);
    case actionTypes.GET_EMERALD_CONFIG_SUCCESS:
    case actionTypes.GET_EMERALD_SUPPORTCONFIG_SUCCESS:
      return handleInitDataGrabSuccess(state, action);
    case actionTypes.SET_CONTACT_SEARCHED_QUERY_TEXT:
      return handleSetContactSearchedQueryText(state, action);
    case actionTypes.FETCH_ALCHEMY_SUCCESS_3P:
    case actionTypes.FETCH_ALCHEMY_SUCCESS:
    case actionTypes.FETCH_ALCHEMY_ERROR:
      return handleDeepLinkUpdate(state, action);
    case actionTypes.SET_IS_AUTOSUGGEST_CARD_SEARCH:
      return handleIsAutoSuggestCardSearch(state, action);
    case actionTypes.RESET_SEARCH_SESSION:
      return handleSearchSessionReset(state);
    case actionTypes.FETCH_REGION_INFO_SUCCESS:
      return handleFetchRegionInfoSuccess(state, action);
    case actionTypes.FETCH_REGION_INFO_ERROR:
      return handleRegionInfoError(state);
    case actionTypes.UPDATE_DEEPLINK:
      return handleDeepLinkPathUpdate(state, action);
    case actionTypes.RESET_CONTACT_US_FOCUS:
      return resetContactUsFocus(state, action);
    default:
      return state;
  }
};
